import { useEffect } from "react";
import useAppStore from "@/store/useAppStore";

/**
 * Phase G (Δ-11) — global 1s tick driver.
 *
 * One `setInterval(1000)` per app lifetime. Bumps `useAppStore.tick`
 * so timer-bearing cards re-derive their elapsed/waiting strings.
 *
 * Behaviour locks (see CONSTRAINTS C-16, C-17):
 *  - Runs only while `document.visibilityState === 'visible'`.
 *  - On `visibilitychange` → `visible`, force-bumps once immediately
 *    (covers browser-tab throttling — R-G-01 in phase_g_discovery.md).
 *  - Cleans up on unmount (clears interval + removes listener).
 *  - No catch-up loop; missed ticks are acceptable because formatters
 *    read absolute timestamps anyway.
 *
 * Mount once at the top of `<HomePage>`. Calling it from multiple
 * places would multiply the tick rate — don't.
 *
 * CR-0019 (Δ-18) — interval handle hoisted to module scope so the
 * exported `resyncTick()` can clear+restart it from outside React
 * (e.g. from `useReserveTable.onSuccess`). This realigns the tick's
 * phase to the reservation moment, eliminating the up-to-2s "0s"
 * stall caused by interval-phase aliasing against `Math.floor`. The
 * single-fanout architecture (C-17) is preserved — still ONE global
 * interval, not per-card.
 */

let _intervalId = null;

function _tickOnce() {
  useAppStore.getState().bumpTick();
}

function _start() {
  if (_intervalId !== null) return;
  _intervalId = setInterval(_tickOnce, 1000);
}

function _stop() {
  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}

/**
 * CR-0019 (Δ-18) — phase-realign the global 1s tick AND force one
 * immediate bump. Called from the `onSuccess` of mutations that
 * start a waiting timer (Reserve / Reassign / ReserveFromOccupied)
 * so the first visible second lands at exactly +1.0s from the
 * reservation moment, not at the next "natural" beat of the
 * global interval (which used to be up to ~2.0s away).
 *
 * Idempotent and safe at all times:
 *  - Tab hidden (interval stopped by visibilitychange handler):
 *    bumpTick still fires, but no interval to restart — the
 *    visibilitychange→visible handler will start it cleanly on
 *    next focus.
 *  - Tab visible (interval running): clears and restarts so the
 *    next beat lands at `Date.now() + 1000`.
 */
export function resyncTick() {
  _tickOnce();
  if (_intervalId !== null) {
    clearInterval(_intervalId);
    _intervalId = setInterval(_tickOnce, 1000);
  }
}

export default function useTimerTick() {
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // Force-bump so the UI immediately reflects time elapsed while
        // the tab was hidden, then resume the interval.
        _tickOnce();
        _start();
      } else {
        _stop();
      }
    };

    if (document.visibilityState === "visible") {
      _start();
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      _stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
}
